import { useEffect, useState } from 'react';
import * as Icon from './Icons';

/**
 * Powłoka aplikacji: boczna nawigacja, okruszki, treść ekranu — wspólna dla aplikacji klienta
 * i panelu operatora.
 *
 * Panel ma wyglądać dokładnie jak aplikacja, więc nie kopiujemy układu, tylko go współdzielimy:
 * te same klasy (app-shell.css), ta sama struktura znaczników. Powłoka nie wie nic o firmach,
 * rolach ani słownikach — dostaje gotowe napisy i zdarzenia, a decyzje „co pokazać" zostają
 * po stronie aplikacji.
 */

export type NavEntry = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Licznik przy pozycji — tylko tam, gdzie liczbę naprawdę mamy. Zmyślony licznik jest gorszy niż jego brak. */
  badge?: number;
};

export type NavGroup = { label: string | null; entries: NavEntry[] };

/** Inicjały do plakietki konta: pierwsze litery dwóch pierwszych słów, albo początek nazwy. */
export function initials(name: string): string {
  const words = name.split(/[\s.,-]+/).filter((word) => /\p{L}/u.test(word));
  const letters = words.slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '');
  return letters.join('') || name.slice(0, 2).toUpperCase();
}

export type Density = 'comfortable' | 'dense';

/**
 * Gęstość układu pamiętana między sesjami i ustawiana na `<html>`, bo dotyczy także ekranów
 * poza powłoką. Klucz w pamięci jest parametrem: aplikacja i panel to osobne domeny, ale nazwa
 * ma być spójna.
 */
export function useDensity(storageKey = 'firmeto-gestosc'): [Density, (next: Density) => void] {
  const [density, setDensity] = useState<Density>(() => {
    try {
      return window.localStorage.getItem(storageKey) === 'dense' ? 'dense' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });

  useEffect(() => {
    document.documentElement.dataset.density = density;
    try {
      window.localStorage.setItem(storageKey, density);
    } catch {
      // Tryb prywatny bez pamięci lokalnej — zostajemy przy wyborze na czas tej sesji
    }
  }, [density, storageKey]);

  return [density, setDensity];
}

export function AppShell({
  brand,
  context,
  groups,
  current,
  onSelect,
  crumbs,
  controls,
  account,
  navLabel,
  children,
}: {
  /** Nazwa w lewym górnym rogu; znak marki jest zawsze ten sam. */
  brand: string;
  /** Przełącznik kontekstu pod marką — w aplikacji firma i rola; w panelu może go nie być. */
  context?: { title: string; subtitle: string; onClick?: () => void };
  groups: NavGroup[];
  current: string;
  onSelect: (key: string) => void;
  /** Okruszki w pasku górnym — gotowe elementy, bo ich treść zna tylko aplikacja. */
  crumbs: React.ReactNode;
  /** Prawa strona paska górnego: gęstość, język, wylogowanie. */
  controls: React.ReactNode;
  account: { fullName: string; role: string };
  navLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <Icon.Mark />
          <b>{brand}</b>
        </div>

        {context && (
          <button type="button" className="switch" onClick={context.onClick} disabled={!context.onClick}>
            <span className="tx">
              <b>{context.title}</b>
              <em>{context.subtitle}</em>
            </span>
            {context.onClick && <Icon.Chevron />}
          </button>
        )}

        <nav aria-label={navLabel}>
          {groups
            .filter((group) => group.entries.length > 0)
            .map((group, index) => (
              <div className="navgrp" key={group.label ?? index}>
                {group.label && <span className="lbl">{group.label}</span>}
                {group.entries.map((entry) => (
                  <button
                    type="button"
                    className="nv"
                    key={entry.key}
                    aria-current={entry.key === current ? 'page' : undefined}
                    onClick={() => onSelect(entry.key)}
                  >
                    <entry.icon />
                    {entry.label}
                    {entry.badge ? <span className="b">{entry.badge}</span> : null}
                  </button>
                ))}
              </div>
            ))}
        </nav>

        <div className="me">
          <i>{initials(account.fullName)}</i>
          <span className="tx">
            <b>{account.fullName}</b>
            <em>{account.role}</em>
          </span>
        </div>
      </aside>

      <div className="main">
        <div className="top">
          <div className="crumb">{crumbs}</div>
          {controls}
        </div>
        {/* Odstępy treści należą do powłoki, nie do ekranu — każdy ekran zaczyna się od nagłówka */}
        <div className="body">{children}</div>
      </div>
    </div>
  );
}
